/**
 * Manifest diffing — comparing two AtlasManifest snapshots (doc 147 §9 Phase 4).
 *
 * Powers the diff and drift routes: shows what changed between two manifest
 * versions across all top-level arrays (domains, entities, references, etc.).
 *
 * @see docs/impl/147-brisarx-atlas-design.html §9 Phase 4
 */

import type {
  AtlasManifest,
  Domain,
  Entity,
  Reference,
  Surface,
  EventType,
  CloudFunction,
  StateMachine,
  FirestorePath,
  TagDefinition,
} from './types.js';

export type DiffOp = 'added' | 'removed' | 'changed';

/**
 * Field-level change within a single object comparison.
 */
export interface PropertyChange {
  field: string;
  change: 'added' | 'removed' | 'changed';
  from?: unknown;
  to?: unknown;
}

/**
 * A single diff entry for an item within a manifest array.
 */
export interface DiffEntry<T> {
  /** The operation: 'added' (in after but not before), 'removed' (in before but not after), 'changed' (in both but different). */
  op: DiffOp;
  /** The stable id of the item — e.g. entity name, domain id, reference source↔target. */
  id: string;
  /** The item's content in the before snapshot; absent for 'added'. */
  before?: T;
  /** The item's content in the after snapshot; absent for 'removed'. */
  after?: T;
}

/**
 * Complete diff report across all manifest array types, with summary counts.
 */
export interface ManifestDiffReport {
  domains: DiffEntry<Domain>[];
  entities: DiffEntry<Entity>[];
  references: DiffEntry<Reference>[];
  surfaces: DiffEntry<Surface>[];
  events: DiffEntry<EventType>[];
  cloudFunctions: DiffEntry<CloudFunction>[];
  stateMachines: DiffEntry<StateMachine>[];
  paths: DiffEntry<FirestorePath>[];
  tags: DiffEntry<TagDefinition>[];

  /** Total number of items added across all arrays. */
  added: number;
  /** Total number of items removed across all arrays. */
  removed: number;
  /** Total number of items changed across all arrays. */
  changed: number;
  /** Total count of all operations. */
  total: number;
}

/**
 * Build a stable id for a reference (from↔to pair).
 */
function referenceId(ref: Reference): string {
  return `${ref.from}→${ref.to}`;
}

/**
 * Check whether two items are identical using JSON serialization.
 * Simple but sufficient for this use case — manifest items are POJOs with no circular refs.
 */
function isEqual<T>(a: T, b: T): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Diff a single array type, producing DiffEntry[] for added, removed, changed items.
 *
 * @param before Array of items in the baseline snapshot
 * @param after Array of items in the target snapshot
 * @param getId Function to extract the stable id from an item
 * @returns Array of DiffEntry<T> for all changes
 */
function diffArray<T>(
  before: T[],
  after: T[],
  getId: (item: T) => string,
): DiffEntry<T>[] {
  const entries: DiffEntry<T>[] = [];
  const beforeMap = new Map(before.map((item) => [getId(item), item]));
  const afterMap = new Map(after.map((item) => [getId(item), item]));

  // Removed items (in before, not in after)
  for (const [id, item] of beforeMap.entries()) {
    if (!afterMap.has(id)) {
      entries.push({ op: 'removed', id, before: item });
    }
  }

  // Added items (in after, not in before)
  for (const [id, item] of afterMap.entries()) {
    if (!beforeMap.has(id)) {
      entries.push({ op: 'added', id, after: item });
    }
  }

  // Changed items (in both, but different)
  for (const [id, itemBefore] of beforeMap.entries()) {
    const itemAfter = afterMap.get(id);
    if (itemAfter && !isEqual(itemBefore, itemAfter)) {
      entries.push({ op: 'changed', id, before: itemBefore, after: itemAfter });
    }
  }

  return entries;
}

/**
 * Compute the complete diff between two manifest snapshots.
 *
 * Diffs all top-level arrays by their stable id fields and detects:
 * - 'added': entries present in `after` but not `before`
 * - 'removed': entries present in `before` but not `after`
 * - 'changed': entries in both with different JSON content
 *
 * @param before The baseline manifest (e.g. the old commit's snapshot)
 * @param after The target manifest (e.g. the new commit's snapshot)
 * @returns A complete DiffReport with entries for each array type and summary counts
 */
export function computeManifestDiff(before: AtlasManifest, after: AtlasManifest): ManifestDiffReport {
  const domains = diffArray(before.domains, after.domains, (d) => d.id);
  const entities = diffArray(before.entities, after.entities, (e) => e.id);
  const references = diffArray(
    before.references,
    after.references,
    (r) => referenceId(r),
  );
  const surfaces = diffArray(before.surfaces, after.surfaces, (s) => s.id);
  const events = diffArray(before.events, after.events, (e) => e.type);
  const cloudFunctions = diffArray(before.cloudFunctions, after.cloudFunctions, (cf) => cf.name);
  const stateMachines = diffArray(before.stateMachines, after.stateMachines, (sm) => sm.id);
  const paths = diffArray(before.paths, after.paths, (p) => p.path);
  const tags = diffArray(before.tags, after.tags, (t) => t.tag);

  // Count operations across all arrays
  const countByOp = (entries: DiffEntry<unknown>[], op: DiffOp): number =>
    entries.filter((e) => e.op === op).length;

  const added =
    countByOp(domains, 'added') +
    countByOp(entities, 'added') +
    countByOp(references, 'added') +
    countByOp(surfaces, 'added') +
    countByOp(events, 'added') +
    countByOp(cloudFunctions, 'added') +
    countByOp(stateMachines, 'added') +
    countByOp(paths, 'added') +
    countByOp(tags, 'added');

  const removed =
    countByOp(domains, 'removed') +
    countByOp(entities, 'removed') +
    countByOp(references, 'removed') +
    countByOp(surfaces, 'removed') +
    countByOp(events, 'removed') +
    countByOp(cloudFunctions, 'removed') +
    countByOp(stateMachines, 'removed') +
    countByOp(paths, 'removed') +
    countByOp(tags, 'removed');

  const changed =
    countByOp(domains, 'changed') +
    countByOp(entities, 'changed') +
    countByOp(references, 'changed') +
    countByOp(surfaces, 'changed') +
    countByOp(events, 'changed') +
    countByOp(cloudFunctions, 'changed') +
    countByOp(stateMachines, 'changed') +
    countByOp(paths, 'changed') +
    countByOp(tags, 'changed');

  return {
    domains,
    entities,
    references,
    surfaces,
    events,
    cloudFunctions,
    stateMachines,
    paths,
    tags,
    added,
    removed,
    changed,
    total: added + removed + changed,
  };
}

/**
 * Field-level detail for a single changed object — used by DiffView to expand
 * a "changed" row (before/after snapshots already exist on every DiffEntry,
 * this just compares them key-by-key for display), and by the AI-edit flow to
 * show a reviewer exactly what one proposed update-* op's patch touches.
 */
export function diffObject<T extends Record<string, unknown>>(before: T, after: T): PropertyChange[] {
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  const changes: PropertyChange[] = [];
  for (const key of keys) {
    const beforeVal = before[key];
    const afterVal = after[key];
    if (JSON.stringify(beforeVal) === JSON.stringify(afterVal)) continue;
    const change = !(key in before) ? 'added' : !(key in after) ? 'removed' : 'changed';
    changes.push({ field: key, change, from: beforeVal, to: afterVal });
  }
  return changes;
}
